/**
 * Timezone Utility Functions
 *
 * Utilities for converting between UTC and local timezones using Luxon
 * All timestamps are stored in UTC and converted for display purposes
 */

import { DateTime } from 'luxon';
import { Factory } from '@/schemas/factory';

/**
 * Validates and creates a DateTime object from an ISO string
 * @param isoString - The ISO date string to validate
 * @param zone - The timezone for the DateTime object
 * @returns Valid DateTime object
 * @throws Error if the input date is invalid
 */
function createValidDateTime(isoString: string, zone: string = 'utc'): DateTime {
  const dt = DateTime.fromISO(isoString, { zone });

  if (!dt.isValid) {
    throw new Error(`Invalid date: ${isoString}. Reason: ${dt.invalidReason}`);
  }

  return dt;
}

/**
 * Converts a UTC ISO date string into the factory's local timezone.
 * @param utcDate - The UTC date string in ISO format
 * @param factoryTimezone - The IANA timezone string for the factory
 * @param locale - Optional locale string (defaults to browser locale)
 * @returns The formatted date string in the factory's local timezone with locale-aware formatting
 */
export function toFactoryTimezone(
  utcDate: string,
  factoryTimezone: string,
  locale?: string
): string {
  const dt = createValidDateTime(utcDate, 'utc').setZone(factoryTimezone);
  return dt.toLocaleString(DateTime.DATETIME_MED, { locale });
}

/**
 * Converts a UTC ISO date string into the user's local timezone if provided.
 * @param utcDate - The UTC date string in ISO format
 * @param userTimezone - Optional IANA timezone string for the user
 * @param locale - Optional locale string (defaults to browser locale)
 * @returns The formatted date string in the user's local timezone with locale-aware formatting
 */
export function toUserTimezone(utcDate: string, userTimezone?: string, locale?: string): string {
  const zone = userTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const dt = createValidDateTime(utcDate, 'utc').setZone(zone);
  return dt.toLocaleString(DateTime.DATETIME_MED, { locale });
}

/**
 * Converts a local date to UTC for saving to the backend.
 * @param localDate - The local date string in ISO format
 * @param timezone - The IANA timezone string of the local date
 * @returns The UTC date string in ISO format
 * @throws Error if the input date is invalid or timezone is invalid
 */
export function fromLocalToUtc(localDate: string, timezone: string): string {
  const dt = createValidDateTime(localDate, timezone);
  const utcDt = dt.toUTC();
  const isoString = utcDt.toISO();

  if (!isoString) {
    throw new Error(`Failed to convert date to UTC ISO string: ${localDate}`);
  }

  return isoString;
}

/**
 * Get the current UTC time as an ISO string
 * @returns Current UTC time in ISO format
 * @throws Error if unable to get current UTC time
 */
export function getCurrentUtcTime(): string {
  const utcDt = DateTime.utc();
  const isoString = utcDt.toISO();

  if (!isoString) {
    throw new Error('Failed to get current UTC time as ISO string');
  }

  return isoString;
}

/**
 * Format a UTC date string for display with custom format
 * @param utcDate - The UTC date string in ISO format
 * @param timezone - The target timezone
 * @param format - Luxon format string (default: locale-aware)
 * @param locale - Optional locale string (defaults to browser locale)
 * @returns Formatted date string
 */
export function formatUtcDate(
  utcDate: string,
  timezone: string,
  format?: string,
  locale?: string
): string {
  const dt = createValidDateTime(utcDate, 'utc').setZone(timezone);

  if (format) {
    return dt.toFormat(format);
  }

  return dt.toLocaleString(DateTime.DATETIME_MED, { locale });
}

/**
 * Get user's browser timezone
 * @returns The user's browser timezone string
 */
export function getUserBrowserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Check if a timezone string is valid
 * @param timezone - The timezone string to validate
 * @returns True if the timezone is valid
 */
export function isValidTimezone(timezone: string): boolean {
  try {
    return DateTime.now().setZone(timezone).isValid;
  } catch {
    return false;
  }
}

/**
 * Get timezone offset information
 * @param timezone - The timezone string
 * @returns Object with offset information
 */
export function getTimezoneOffset(timezone: string): {
  offset: string;
  offsetMinutes: number;
  offsetHours: number;
} {
  const dt = DateTime.now().setZone(timezone);
  const offsetMinutes = dt.offset;
  const offsetHours = offsetMinutes / 60;
  const offset = dt.toFormat('ZZ');

  return {
    offset,
    offsetMinutes,
    offsetHours,
  };
}

/**
 * Validate a factory's timezone against the Factory schema
 * @param factory - The factory object to validate
 * @returns True if the factory has a valid timezone
 */
export function validateFactoryTimezone(factory: Factory): boolean {
  return isValidTimezone(factory.settings.timezone);
}

/**
 * Format date for display with different locale-aware options
 * @param utcDate - The UTC date string in ISO format
 * @param timezone - The target timezone
 * @param style - Display style: 'short', 'medium', 'long', 'full'
 * @param locale - Optional locale string (defaults to browser locale)
 * @returns Formatted date string
 */
export function formatDateWithStyle(
  utcDate: string,
  timezone: string,
  style: 'short' | 'medium' | 'long' | 'full' = 'medium',
  locale?: string
): string {
  const dt = createValidDateTime(utcDate, 'utc').setZone(timezone);

  switch (style) {
    case 'short':
      return dt.toLocaleString(DateTime.DATETIME_SHORT, { locale });
    case 'medium':
      return dt.toLocaleString(DateTime.DATETIME_MED, { locale });
    case 'long':
      return dt.toLocaleString(DateTime.DATETIME_HUGE, { locale });
    case 'full':
      return dt.toLocaleString(DateTime.DATETIME_FULL, { locale });
    default:
      return dt.toLocaleString(DateTime.DATETIME_MED, { locale });
  }
}

/**
 * Format time only with locale-aware formatting
 * @param utcDate - The UTC date string in ISO format
 * @param timezone - The target timezone
 * @param locale - Optional locale string (defaults to browser locale)
 * @returns Formatted time string
 */
export function formatTimeOnly(utcDate: string, timezone: string, locale?: string): string {
  const dt = createValidDateTime(utcDate, 'utc').setZone(timezone);
  return dt.toLocaleString(DateTime.TIME_SIMPLE, { locale });
}

/**
 * Format date only with locale-aware formatting
 * @param utcDate - The UTC date string in ISO format
 * @param timezone - The target timezone
 * @param locale - Optional locale string (defaults to browser locale)
 * @returns Formatted date string
 */
export function formatDateOnly(utcDate: string, timezone: string, locale?: string): string {
  const dt = createValidDateTime(utcDate, 'utc').setZone(timezone);
  return dt.toLocaleString(DateTime.DATE_MED, { locale });
}

/**
 * Get user's browser locale
 * @returns The user's browser locale string
 */
export function getUserBrowserLocale(): string {
  return Intl.DateTimeFormat().resolvedOptions().locale;
}

/**
 * Convert app language code to full locale for date formatting
 * @param languageCode - The app language code (e.g., 'en', 'pt')
 * @returns Full locale string for date formatting
 */
export function getLocaleFromLanguage(languageCode: string): string {
  const localeMap: Record<string, string> = {
    en: 'en-US', // English -> US format (MM/DD/YYYY)
    pt: 'pt-PT', // Portuguese -> Portugal format (DD/MM/YYYY)
  };

  return localeMap[languageCode] || 'en-US'; // Fallback to US format
}

/**
 * Get user's preferred locale for date formatting
 * This combines the user's language preference with regional formatting
 * @param userLanguage - The user's language preference from i18n
 * @returns Locale string for date formatting
 */
export function getUserDateFormatLocale(userLanguage?: string): string {
  if (userLanguage) {
    return getLocaleFromLanguage(userLanguage);
  }

  // Fallback to browser locale if no user language preference
  return getUserBrowserLocale();
}
